import type { Room, Participant } from '../types';
import type { ParticipantData, RoomData } from '../types/meeting';
import { getRoom } from '../services/firebase/firestore';
import {
  getConnectionState,
  getCurrentRoomCameraState,
  getCurrentScreenShareState,
  getLocalParticipant,
  getParticipants,
  getRemoteParticipant,
  setCallbacks,
} from '../services/livekit';
import { logDebug } from '../services/logging/logger';
import {
  mapParticipant,
  removeParticipant,
  upsertParticipant,
  updateLocalParticipantTrack,
  updateParticipantSharing,
  updateParticipantTrack,
} from './meetingStoreParticipants';
import type { MeetingStoreGet, MeetingStoreSet } from './meetingStoreTypes';

const RECONCILE_INTERVAL_MS = 1000;
const RECONCILE_RETRY_DELAYS_MS = [0, 120, 320];

let reconcileIntervalHandle: ReturnType<typeof setInterval> | null = null;
const reconcileTimeoutHandles = new Set<ReturnType<typeof setTimeout>>();

/**
 * Loads room metadata or throws if the room is missing.
 */
export async function fetchRoomOrThrow(roomCode: string): Promise<RoomData> {
  const roomData = await getRoom(roomCode);
  if (!roomData) {
    throw new Error('Room not found');
  }
  return roomData;
}

/**
 * Maps Firestore room data into local store state.
 */
export function mapRoomData(roomData: RoomData): Room {
  return {
    roomCode: roomData.roomCode,
    roomName: roomData.roomName,
    hostUserId: roomData.hostUserId,
    createdAt: roomData.createdAt,
    maxParticipants: roomData.maxParticipants,
  };
}
/**
 * Builds initial participant state after joining a call.
 */
export function buildParticipantState(): {
  participants: Participant[];
  localParticipant: Participant | null;
} {
  const local = getLocalParticipant();
  const participants = getParticipants()
    .filter((participant) => participant.participantId !== local?.participantId)
    .map((participant) => mapParticipant(participant));

  return {
    participants,
    localParticipant: local ? mapParticipant(local) : null,
  };
}

/**
 * Rebuilds meeting state from current LiveKit participant/track publications.
 * This is the authoritative repair path when event delivery is missed.
 */
export function reconcileLiveKitState(set: MeetingStoreSet): void {
  const { participants, localParticipant } = buildParticipantState();
  const screenShare = getCurrentScreenShareState();
  const roomCamera = getCurrentRoomCameraState();
  const connection = getConnectionState();
  const isConnected = connection === 'connected' || connection === 'reconnecting';
  const localIsSharing = localParticipant?.participantId === screenShare.ownerId;

  set({
    participants,
    localParticipant: localParticipant
      ? { ...localParticipant, isSharingScreen: localIsSharing }
      : null,
    isConnected,
    isSharingScreen: screenShare.ownerId != null,
    screenShareOwnerId: screenShare.ownerId,
    screenShareTrack: screenShare.track,
    roomCameraOwnerId: roomCamera.ownerId,
    roomCameraTrack: roomCamera.track,
  });
}

/**
 * Starts periodic reconciliation while a room session is active.
 */
export function startLiveKitStateReconciler(
  set: MeetingStoreSet
): void {
  stopLiveKitStateReconciler();
  scheduleLiveKitReconcile(set, RECONCILE_RETRY_DELAYS_MS);

  reconcileIntervalHandle = setInterval(() => {
    if (getConnectionState() === 'disconnected') {
      return;
    }

    reconcileLiveKitState(set);
  }, RECONCILE_INTERVAL_MS);
}

/**
 * Stops periodic reconciliation timers.
 */
export function stopLiveKitStateReconciler(): void {
  if (reconcileIntervalHandle) {
    clearInterval(reconcileIntervalHandle);
    reconcileIntervalHandle = null;
  }

  for (const timeoutHandle of reconcileTimeoutHandles) {
    clearTimeout(timeoutHandle);
  }
  reconcileTimeoutHandles.clear();
}
/**
 * Registers LiveKit callbacks for participant and track updates.
 */
export function registerLiveKitCallbacks(set: MeetingStoreSet, get: MeetingStoreGet): void {
  setCallbacks({
    onParticipantJoined: (participant) => {
      handleParticipantJoined(participant, set, get);
      scheduleLiveKitReconcile(set);
    },
    onParticipantLeft: (participant) => {
      handleParticipantLeft(participant, set, get);
      scheduleLiveKitReconcile(set);
    },
    onParticipantUpdated: (participant) => {
      handleParticipantUpdated(participant, set, get);
      scheduleLiveKitReconcile(set);
    },
    onTrackStarted: (participantId, track, trackType) => {
      if (!track) {
        return;
      }

      handleTrackStarted(participantId, track, trackType, set, get);
      scheduleLiveKitReconcile(set, RECONCILE_RETRY_DELAYS_MS);
    },
    onTrackStopped: (participantId, _track, trackType) => {
      handleTrackStopped(participantId, trackType, set, get);
      scheduleLiveKitReconcile(set, RECONCILE_RETRY_DELAYS_MS);
    },
    onConnectionStateChanged: (state) => {
      const isConnected = state === 'connected' || state === 'reconnecting';
      const isConnecting = state === 'connecting' || state === 'reconnecting';
      set({ isConnected, isConnecting });
      scheduleLiveKitReconcile(set, RECONCILE_RETRY_DELAYS_MS);
    },
    onError: (error) => {
      set({ error });
    },
  });
}
function handleParticipantJoined(
  participant: ParticipantData,
  set: MeetingStoreSet,
  get: MeetingStoreGet
): void {
  if (participant.isLocal) {
    const { localParticipant } = get();
    set({
      localParticipant: mapParticipant(participant, localParticipant || undefined),
    });
    return;
  }

  const { participants } = get();
  set({ participants: upsertParticipant(participants, participant) });
}

function handleParticipantLeft(
  participant: ParticipantData,
  set: MeetingStoreSet,
  get: MeetingStoreGet
): void {
  const { participants, screenShareOwnerId } = get();
  const nextParticipants = removeParticipant(participants, participant.participantId);

  set({
    participants: nextParticipants,
    localParticipant:
      participant.isLocal ? null : get().localParticipant,
    screenShareOwnerId:
      screenShareOwnerId === participant.participantId ? null : screenShareOwnerId,
    screenShareTrack:
      screenShareOwnerId === participant.participantId ? null : get().screenShareTrack,
    isSharingScreen:
      screenShareOwnerId === participant.participantId ? false : get().isSharingScreen,
  });
}

function handleParticipantUpdated(
  participant: ParticipantData,
  set: MeetingStoreSet,
  get: MeetingStoreGet
): void {
  const { participants, localParticipant } = get();
  if (participant.isLocal) {
    set({
      localParticipant: mapParticipant(participant, localParticipant || undefined),
    });
    return;
  }

  const updatedParticipants = upsertParticipant(participants, participant);

  set({
    participants: updatedParticipants,
    localParticipant,
  });
}

function handleTrackStarted(
  participantId: string,
  track: MediaStreamTrack,
  trackType: 'audio' | 'video' | 'screenVideo' | 'screenAudio' | 'roomCamera',
  set: MeetingStoreSet,
  get: MeetingStoreGet
): void {
  if (trackType === 'screenVideo') {
    set((state) => ({
      isSharingScreen: true,
      screenShareOwnerId: participantId,
      screenShareTrack: track,
      participants: updateParticipantSharing(state.participants, participantId, true),
      localParticipant: state.localParticipant
        ? {
            ...state.localParticipant,
            isSharingScreen: state.localParticipant.participantId === participantId,
          }
        : state.localParticipant,
    }));
    return;
  }

  if (trackType === 'screenAudio') {
    return;
  }

  if (trackType === 'roomCamera') {
    logDebug('Room camera track received', {
      participantId,
      trackId: track.id,
      readyState: track.readyState,
    });

    set({
      roomCameraTrack: track,
      roomCameraOwnerId: participantId,
    });
    return;
  }

  const { participants, localParticipant } = get();
  const isLocal = localParticipant?.participantId === participantId;
  let nextParticipants = participants;

  if (!isLocal && !participants.some((p) => p.participantId === participantId)) {
    const remote = getRemoteParticipant(participantId);
    if (remote) {
      nextParticipants = upsertParticipant(participants, remote);
    } else {
      const fallback = {
        participantId,
        displayName: participantId,
        isVR: false,
        isHost: false,
        isMuted: false,
        isCameraOn: true,
        isSharingScreen: false,
      };
      nextParticipants = upsertParticipant(participants, fallback);
    }
  }

  set({
    participants: updateParticipantTrack(nextParticipants, participantId, trackType, track),
    localParticipant: updateLocalParticipantTrack(
      localParticipant,
      participantId,
      trackType,
      track
    ),
  });
}

function handleTrackStopped(
  participantId: string,
  trackType: 'audio' | 'video' | 'screenVideo' | 'screenAudio' | 'roomCamera',
  set: MeetingStoreSet,
  get: MeetingStoreGet
): void {
  if (trackType === 'screenVideo') {
    set((state) => ({
      isSharingScreen:
        state.screenShareOwnerId === participantId ? false : state.isSharingScreen,
      screenShareOwnerId:
        state.screenShareOwnerId === participantId ? null : state.screenShareOwnerId,
      screenShareTrack:
        state.screenShareOwnerId === participantId ? null : state.screenShareTrack,
      participants: updateParticipantSharing(state.participants, participantId, false),
      localParticipant: state.localParticipant
        ? {
            ...state.localParticipant,
            isSharingScreen:
              state.localParticipant.participantId === participantId
                ? false
                : state.localParticipant.isSharingScreen,
          }
        : state.localParticipant,
    }));
    return;
  }

  if (trackType === 'screenAudio') {
    return;
  }

  if (trackType === 'roomCamera') {
    set({
      roomCameraTrack: null,
      roomCameraOwnerId: null,
    });
    return;
  }

  const { participants, localParticipant } = get();
  set({
    participants: updateParticipantTrack(participants, participantId, trackType, null),
    localParticipant: updateLocalParticipantTrack(
      localParticipant,
      participantId,
      trackType,
      null
    ),
  });
}

function scheduleLiveKitReconcile(
  set: MeetingStoreSet,
  delaysMs: number[] = [0]
): void {
  const uniqueDelays = Array.from(new Set(delaysMs.filter((value) => value >= 0)));
  for (const delayMs of uniqueDelays) {
    const timeoutHandle = setTimeout(() => {
      reconcileTimeoutHandles.delete(timeoutHandle);
      if (getConnectionState() === 'disconnected') {
        return;
      }

      reconcileLiveKitState(set);
    }, delayMs);

    reconcileTimeoutHandles.add(timeoutHandle);
  }
}
